import React, { useEffect, useMemo, useState } from 'react'
import { AxiosResponse } from 'axios'
import { Folder } from '../../../../models/Infrastructure/types'
import {
    AreaConfig,
    AreaStatusTag,
    ezScheduleDetails,
    ListVendor,
    ModelMap,
    Service,
    TemplateWorkorder,
    UnitConfig,
} from '../../../../models'
import { pruneInfrastructure } from '../../../../components/TemplateScheduleGantt/Components/DetailColumnServiceRow/ServiceSpecifyUnitsModal'
import { UnitCoverage } from '../../../../models/WorkOrder/types'
import { Container } from '../../../../components'
import { Dialog } from '@material-ui/core'
import {
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button,
    MenuItem,
    TextField,
    LinearProgress,
    Grid,
    Theme,
} from '@material-ui/core'
import { TemplateScheduleGantt } from '../../../../components/TemplateScheduleGantt/TemplateScheduleGantt'

import { useScheduleReviewStyles } from './styles'
import {
    UpdateEzScheduleParams,
    TemplateWorkordersByPhaseEntry,
    UnitCoverageByPhaseEntry,
    UpdateTemplateServiceVendorParams,
} from '../../../../hooks/useEzSchedule/types'
import { ReviewHeader } from './components/ReviewHeader'
import { TurnReviewDialog } from './components/TurnReviewDialog'
import { EzosLoader } from '../../../../components/EzosLoader/EzosLoader'
interface Props {
    theme: Theme
    ezSchedule: ezScheduleDetails
    ezScheduleLoading: boolean
    ezScheduleFetching: boolean
    templateWorkorders: TemplateWorkorder[] | undefined
    unitCoverage?: UnitCoverage
    selectedPhaseId?: number
    schedulePhases: ezScheduleDetails['schedule_phases']
    templateWorkordersByPhase: TemplateWorkordersByPhaseEntry[]
    unitCoverageByPhase: UnitCoverageByPhaseEntry[]
    templateIsValid: boolean | null
    generateWorkorders: (
        phaseIds: number[],
        deleteExistingWorkorders: boolean,
        creationSource: string,
    ) => Promise<AxiosResponse>
    serviceList: Service[]
    vendorList: ListVendor[]
    infrastructure: Folder
    unitConfigMap: ModelMap<UnitConfig>
    areaConfigMap: ModelMap<AreaConfig>
    areaStatusTagMap: ModelMap<AreaStatusTag>
    updateEzSchedule: (params: UpdateEzScheduleParams) => void
    updateTemplateServiceVendor: (
        params: UpdateTemplateServiceVendorParams,
    ) => void
    generateTemplateWorkorders: () => void
    templateWorkordersLoading: boolean
    generateTemplateWorkordersLoading: boolean
    onCloseModal: () => void
    reloadScheduleWorkorders?: () => void | Promise<void>
    workordersLoading?: boolean
    templateCreationSource: string
    onAfterSchedule?: () => void
}

interface DeleteExistingWorkordersHelperProps {
    italicTextClass: string
    showTypicalSelection?: boolean
    multiPhaseWarning?: string
}

const DeleteExistingWorkordersHelper = ({
    italicTextClass,
    showTypicalSelection = true,
    multiPhaseWarning,
}: DeleteExistingWorkordersHelperProps) => (
    <>
        {multiPhaseWarning && <div>{multiPhaseWarning}</div>}
        {showTypicalSelection && (
            <div style={multiPhaseWarning ? { marginTop: 8 } : undefined}>
                This is the typical selection.
            </div>
        )}
        <div style={{ marginTop: 8 }}>
            <strong>NOTE:</strong> This will only delete workorders that meet{' '}
            <em>all</em> of the following:
            <ul style={{ margin: '4px 0 0 0', paddingLeft: 20 }}>
                <li>Unlocked</li>
                <li>
                    Service is being scheduled in this run of the auto scheduler
                </li>
                <li>
                    Status is <span className={italicTextClass}>Assigned</span>
                </li>
            </ul>
        </div>
        <div style={{ marginTop: 8 }}>
            If you are scheduling fewer services than before, workorders for
            services you removed from this run will not be deleted
            automatically. Filter by service on your turn board and bulk-delete
            those workorders manually.
        </div>
    </>
)

export const Review = (props: Props) => {
    const {
        theme,
        ezSchedule,
        ezScheduleLoading,
        ezScheduleFetching,
        infrastructure,
        unitConfigMap,
        areaConfigMap,
        areaStatusTagMap,
        templateWorkorders,
        unitCoverage,
        selectedPhaseId,
        schedulePhases,
        templateWorkordersByPhase,
        unitCoverageByPhase,
        templateIsValid,
        generateWorkorders,
        updateEzSchedule,
        serviceList,
        vendorList,
        updateTemplateServiceVendor,
        generateTemplateWorkorders,
        templateWorkordersLoading,
        generateTemplateWorkordersLoading,
        onCloseModal,
        reloadScheduleWorkorders,
        workordersLoading = false,
        templateCreationSource,
        onAfterSchedule,
    } = props
    const classes = useScheduleReviewStyles()
    const [dialogOpen, setDialogOpen] = useState(false)
    const [turnReviewDialogOpen, setTurnReviewDialogOpen] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [submitProgress, setSubmitProgress] = useState(0)
    const [shouldWaitForWorkorders, setShouldWaitForWorkorders] = useState(
        false,
    )
    const [schedulePhaseIdsToSubmit, setSchedulePhaseIdsToSubmit] = useState<
        number[]
    >([])
    const [
        deleteExistingWorkorders,
        setDeleteExistingWorkorders,
    ] = useState<boolean>(!ezSchedule.use_multi_phase)
    const filteredInfrastructure = useMemo(() => {
        const phaseUnits =
            selectedPhaseId == null
                ? ezSchedule.template_units ?? []
                : ezSchedule.phases.find(
                      (phase) => phase.phase.id === selectedPhaseId,
                  )?.template_units ??
                  ezSchedule.template_units ??
                  []
        const includedIds = new Set(
            phaseUnits
                .filter((templateUnit) => templateUnit.is_included)
                .map((templateUnit) => templateUnit.unit.id),
        )
        const pruned = pruneInfrastructure(infrastructure, (unit) =>
            includedIds.has(unit.id),
        )
        return pruned ?? { ...infrastructure, units: [], children: [] }
    }, [infrastructure, ezSchedule, selectedPhaseId])

    useEffect(() => {
        if (!isSubmitting) {
            if (!shouldWaitForWorkorders) {
                setSubmitProgress(0)
            }
            return
        }

        const workorderCount = templateWorkorders?.length ?? 0
        const totalSeconds = Math.max(1, Math.ceil(workorderCount / 100))
        const totalMs = totalSeconds * 1000
        const startTime = Date.now()

        const intervalId = window.setInterval(() => {
            const elapsedMs = Date.now() - startTime
            const nextProgress = Math.min(
                100,
                Math.round((elapsedMs / totalMs) * 100),
            )
            setSubmitProgress(nextProgress)
        }, 200)

        return () => {
            window.clearInterval(intervalId)
        }
    }, [isSubmitting, shouldWaitForWorkorders, templateWorkorders?.length])

    useEffect(() => {
        if (!shouldWaitForWorkorders) {
            return
        }

        if (!workordersLoading) {
            setShouldWaitForWorkorders(false)
            onAfterSchedule?.()
            onCloseModal()
            setDialogOpen(false)
        }
    }, [
        shouldWaitForWorkorders,
        workordersLoading,
        onCloseModal,
        onAfterSchedule,
    ])

    const handleSubmit = async () => {
        setIsSubmitting(true)
        setSubmitProgress(0)
        try {
            const phaseIds =
                schedulePhaseIdsToSubmit.length > 0
                    ? schedulePhaseIdsToSubmit
                    : selectedPhaseId !== undefined
                    ? [selectedPhaseId]
                    : []

            if (phaseIds.length === 0) {
                return
            }

            const response = await generateWorkorders(
                phaseIds,
                deleteExistingWorkorders,
                templateCreationSource,
            )
            if (response?.status === 201) {
                setSubmitProgress(100)
                setShouldWaitForWorkorders(true)
                await Promise.resolve(reloadScheduleWorkorders?.())
            }
        } finally {
            setIsSubmitting(false)
        }
    }

    const handleDialogClose = (
        _event: React.SyntheticEvent,
        _reason?: 'backdropClick' | 'escapeKeyDown',
    ) => {
        if (isSubmitting || shouldWaitForWorkorders) {
            return
        }
        setDialogOpen(false)
    }

    const isProcessing = isSubmitting || shouldWaitForWorkorders

    return (
        <Container className={classes.root}>
            {/* Header */}
            <ReviewHeader
                ezSchedule={ezSchedule}
                templateIsValid={templateIsValid ?? false}
                onOpenTurnReview={() => setTurnReviewDialogOpen(true)}
                generateTemplateWorkorders={generateTemplateWorkorders}
                generateTemplateWorkordersLoading={
                    generateTemplateWorkordersLoading
                }
                templateWorkordersLoading={templateWorkordersLoading}
                selectedPhaseId={selectedPhaseId ?? undefined}
            />
            <Grid
                container
                className={classes.body}
                style={{ position: 'relative' }}
            >
                {ezScheduleFetching && (
                    <div
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            zIndex: 10,
                        }}
                    >
                        <EzosLoader />
                    </div>
                )}
                <Grid
                    item
                    xs={12}
                    style={{
                        display: 'flex',
                        minHeight: 0,
                        height: '100%',
                        opacity: ezScheduleFetching ? 0.4 : 1,
                        pointerEvents: ezScheduleFetching ? 'none' : 'auto',
                        transition: 'opacity 0.3s ease',
                    }}
                >
                    <TemplateScheduleGantt
                        theme={theme}
                        ezSchedule={ezSchedule}
                        infrastructure={filteredInfrastructure}
                        unitConfigMap={unitConfigMap}
                        areaConfigMap={areaConfigMap}
                        areaStatusTagMap={areaStatusTagMap}
                        updateEzSchedule={updateEzSchedule}
                        ezScheduleLoading={ezScheduleLoading}
                        serviceList={serviceList}
                        vendorList={vendorList}
                        templateWorkorders={
                            templateIsValid ? templateWorkorders : undefined
                        }
                        updateTemplateServiceVendor={
                            updateTemplateServiceVendor
                        }
                        selectedPhaseId={selectedPhaseId ?? -1}
                    />
                </Grid>
            </Grid>

            {/* Schedule Workorders Dialog */}
            <Dialog open={dialogOpen} onClose={handleDialogClose}>
                <DialogTitle>Schedule Workorders</DialogTitle>

                <DialogContent>
                    {isProcessing ? (
                        <>
                            <DialogContentText>
                                Scheduling workorders. This will take just a few
                                seconds.
                            </DialogContentText>
                            <LinearProgress
                                variant="determinate"
                                value={submitProgress}
                            />
                        </>
                    ) : (
                        <>
                            <DialogContentText>
                                Upon submission, the workorders will be
                                scheduled and when they finish, this modal will
                                close and the workorders will be on your turn
                                board.
                            </DialogContentText>
                            <DialogContentText>
                                You can choose to delete existing Assigned
                                workorders before scheduling. Only unlocked
                                workorders for services included in this run are
                                affected. Workorders in other statuses are not
                                impacted, no matter what you select.
                            </DialogContentText>

                            <TextField
                                select
                                fullWidth
                                margin="dense"
                                variant="outlined"
                                label="Delete existing assigned workorders?"
                                value={
                                    deleteExistingWorkorders
                                        ? 'delete_existing'
                                        : 'keep_existing'
                                }
                                onChange={(event) =>
                                    setDeleteExistingWorkorders(
                                        event.target.value ===
                                            'delete_existing',
                                    )
                                }
                            >
                                <MenuItem value="keep_existing">
                                    Do not delete any pre-existing assigned
                                    workorders
                                </MenuItem>
                                <MenuItem value="delete_existing">
                                    Delete pre-existing assigned workorders
                                </MenuItem>
                            </TextField>
                            <DialogContentText
                                component="div"
                                style={{
                                    marginTop: 8,
                                    color:
                                        (ezSchedule.use_multi_phase &&
                                            deleteExistingWorkorders) ||
                                        (!ezSchedule.use_multi_phase &&
                                            !deleteExistingWorkorders)
                                            ? '#B71C1C'
                                            : undefined,
                                }}
                            >
                                {deleteExistingWorkorders ? (
                                    <DeleteExistingWorkordersHelper
                                        italicTextClass={
                                            classes.dialogItalicText
                                        }
                                        showTypicalSelection={
                                            !ezSchedule.use_multi_phase
                                        }
                                        multiPhaseWarning={
                                            ezSchedule.use_multi_phase
                                                ? 'Warning: you have a multi-phase turn setup. Unlocked Assigned workorders for services in this run may be deleted across phases.'
                                                : undefined
                                        }
                                    />
                                ) : ezSchedule.use_multi_phase ? (
                                    'This is the typical selection. Either you have no pre-existing assigned workorders, or you intend to keep pre-existing assigned workorders from a different phase.'
                                ) : (
                                    'Warning: if you have pre-existing assigned workorders they will remain and the workorders created here will not take those workorders into consideration, which means you could end up with scheduling conflicts.'
                                )}
                            </DialogContentText>
                        </>
                    )}
                </DialogContent>

                {!isProcessing && (
                    <DialogActions>
                        <Button
                            color="secondary"
                            variant="outlined"
                            className={classes.dialogCancelButton}
                            onClick={() => setDialogOpen(false)}
                        >
                            Cancel
                        </Button>

                        <Button
                            color="primary"
                            variant="contained"
                            className={classes.dialogSubmitButton}
                            onClick={handleSubmit}
                        >
                            Submit
                        </Button>
                    </DialogActions>
                )}
            </Dialog>

            <TurnReviewDialog
                dialogOpen={turnReviewDialogOpen}
                handleDialogClose={() => setTurnReviewDialogOpen(false)}
                templateWorkorders={templateWorkorders}
                unitCoverage={unitCoverage}
                selectedPhaseId={selectedPhaseId}
                schedulePhases={schedulePhases}
                templateWorkordersByPhase={templateWorkordersByPhase}
                unitCoverageByPhase={unitCoverageByPhase}
                onSchedulePlannedWorkorders={(phaseIds: number[]) => {
                    setSchedulePhaseIdsToSubmit(phaseIds)
                    setDeleteExistingWorkorders(!ezSchedule.use_multi_phase)
                    setTurnReviewDialogOpen(false)
                    setDialogOpen(true)
                }}
                ezSchedule={ezSchedule}
            />
        </Container>
    )
}
